// API 側エンティティ(ProcessExecution / StirBatch / ConstructionAction / QualityRecord)から、
// 帳票・進捗集計が期待するモックアップ由来形式へ変換する派生セレクタ群。
//
// 設計方針:
//  - 純関数。入力 db スナップショットを書き換えない。
//  - 数値タイムスタンプは ISO 文字列へ変換し、レポートの fmtDateTime 等に渡せる形にする。
//  - 元エンティティに存在しないフィールドは undefined を返し、帳票側は "-" 等で代替する。

import type { ExtendedDB } from "./extendedDB";

// =========================================================================
// 派生型(再エクスポートで extendedDB.ts から StirExecution 等として参照される)
// =========================================================================

export interface DerivedStirExecution {
  id: string;
  construction_id: string;
  process_execution_ids: string[];
  stir_started_at?: string;
  stir_finished_at?: string;
  stir_duration_seconds?: number;
  mix_ratio_definition_id?: number;
  remarks?: string;
}

export interface DerivedWorkExecution {
  id: string;
  construction_id: string;
  process_execution_ids: string[];
  construction_started_at?: string;
  construction_finished_at?: string;
  construction_duration_seconds?: number;
  actual_qty?: number;
  remarks?: string;
}

export interface DerivedQualityMeasurement {
  id: string;
  // work_id は QualityRecord 自体には含まれないため、construction_id 経由で
  // 帳票側が解決する。互換のため文字列を残すが空文字となる。
  work_id: string;
  construction_id?: string;
  construction_plan_area_id?: string;
  measured_at: string;
  measurement_kind: string;
  material_id?: string;
  temperature?: number;
  humidity?: number;
  moisture_pct?: number;
  thickness_um?: number;
  judgment?: "OK" | "NG" | "-";
  remarks?: string;
  // 旧モックアップ由来で帳票が参照していたが、現状の QualityRecord では復元できないフィールド。
  // 互換維持のため optional のまま残し、常に undefined として返す。
  location_label?: string;
  spot_id?: string;
  mix_actual_main?: number;
  mix_actual_sub?: number;
  pot_life_minutes?: number;
  timing_category?: string;
}

export interface DerivedAreaProcessCompletion {
  id: string;
  construction_id: string;
  area_id: string;
  work_detail_id: number;
  completed_at: string;
}

// extendedDB.ts から名前を流用するため別名再エクスポート。
export type StirExecution = DerivedStirExecution;
export type WorkExecution = DerivedWorkExecution;
export type QualityMeasurement = DerivedQualityMeasurement;
export type AreaProcessCompletion = DerivedAreaProcessCompletion;

// =========================================================================
// 内部ヘルパー
// =========================================================================

function epochToIso(v: number | null | undefined): string | undefined {
  if (v == null) {
    return undefined;
  }
  return new Date(v).toISOString();
}

function epochToIsoOrEmpty(v: number | null | undefined): string {
  const iso = epochToIso(v);
  if (iso == null) {
    return "";
  }
  return iso;
}

function normalizeJudgment(j: string | null | undefined): "OK" | "NG" | "-" | undefined {
  if (j == null) {
    return undefined;
  }
  if (j === "OK" || j === "NG" || j === "-") {
    return j;
  }
  return undefined;
}

// QualityMeasurementItem.name から measurement_kind 文字列を推定する。
// モックアップ仕様の "carry_in_temp_humid"/"construction_temp_humid"/"moisture"/"thickness"/"mix_actual"/"pot_life" にマップ。
function inferMeasurementKind(itemName: string | undefined): string {
  if (!itemName) {
    return "";
  }
  if (itemName.includes("搬入") && (itemName.includes("温") || itemName.includes("湿"))) {
    return "carry_in_temp_humid";
  }
  if (itemName.includes("施工") && (itemName.includes("温") || itemName.includes("湿"))) {
    return "construction_temp_humid";
  }
  if (itemName.includes("含水")) {
    return "moisture";
  }
  if (itemName.includes("膜厚")) {
    return "thickness";
  }
  if (itemName.includes("配合")) {
    return "mix_actual";
  }
  if (itemName.includes("可使時間")) {
    return "pot_life";
  }
  // 既知パターンに該当しなかった: 帳票側で表示はされるが kind 別の振り分けが効かない。
  console.warn(`inferMeasurementKind: 未知の measurement 項目名 "${itemName}" — kind 推定にフォールバックします`);
  return itemName;
}

// =========================================================================
// 派生セレクタ
// =========================================================================

// ProcessExecution.completed_at が記録された行(完了済み)から AreaProcessCompletion 形式へ。
export function deriveAreaProcessCompletions(
  db: Pick<ExtendedDB, "processExecutions">,
): DerivedAreaProcessCompletion[] {
  const out: DerivedAreaProcessCompletion[] = [];
  for (const pe of db.processExecutions) {
    if (pe.deleted_at != null) {
      continue;
    }
    if (pe.completed_at == null) {
      continue;
    }
    out.push({
      id: pe.id,
      construction_id: pe.construction_id,
      area_id: pe.construction_plan_area_id,
      work_detail_id: pe.work_detail_id,
      completed_at: epochToIsoOrEmpty(pe.completed_at),
    });
  }
  return out;
}

// StirBatch を (construction_id, process_execution_id) でグループ化し、StirExecution 形式へ。
export function deriveStirExecutions(
  db: Pick<ExtendedDB, "stirBatches">,
): DerivedStirExecution[] {
  const groups = new Map<string, DerivedStirExecution>();
  for (const sb of db.stirBatches) {
    if (sb.deleted_at != null) {
      continue;
    }
    if (sb.process_execution_id == null) {
      // process_execution_id 未紐付けの StirBatch は集計対象外。silent スキップ防止のため warn。
      console.warn(`deriveStirExecutions: process_execution_id が null の StirBatch をスキップ (id=${sb.id})`);
      continue;
    }
    const key = `${sb.construction_id}::${sb.process_execution_id}`;
    const existing = groups.get(key);
    if (existing == null) {
      groups.set(key, {
        id: sb.id,
        construction_id: sb.construction_id,
        process_execution_ids: [sb.process_execution_id],
        stir_started_at: epochToIso(sb.started_at),
        stir_finished_at: epochToIso(sb.finished_at),
        stir_duration_seconds: sb.duration_seconds ?? undefined,
        mix_ratio_definition_id: sb.mix_ratio_definition_id ?? undefined,
        remarks: sb.remarks ?? undefined,
      });
      continue;
    }
    // 同一プロセス内に複数バッチがある場合は時間幅をマージ。
    const startIso = epochToIso(sb.started_at);
    if (startIso != null) {
      if (existing.stir_started_at == null || startIso < existing.stir_started_at) {
        existing.stir_started_at = startIso;
      }
    }
    const endIso = epochToIso(sb.finished_at);
    if (endIso != null) {
      if (existing.stir_finished_at == null || endIso > existing.stir_finished_at) {
        existing.stir_finished_at = endIso;
      }
    }
  }
  return Array.from(groups.values());
}

// ConstructionAction を 1:1 で WorkExecution 形式へ。
export function deriveWorkExecutions(
  db: Pick<ExtendedDB, "constructionActions">,
): DerivedWorkExecution[] {
  const out: DerivedWorkExecution[] = [];
  for (const ca of db.constructionActions) {
    if (ca.deleted_at != null) {
      continue;
    }
    const peIds: string[] = [];
    if (ca.process_execution_id != null) {
      peIds.push(ca.process_execution_id);
    }
    out.push({
      id: ca.id,
      construction_id: ca.construction_id,
      process_execution_ids: peIds,
      construction_started_at: epochToIso(ca.started_at),
      construction_finished_at: epochToIso(ca.finished_at),
      construction_duration_seconds: ca.duration_seconds ?? undefined,
      actual_qty: ca.actual_qty ?? undefined,
      remarks: ca.remarks ?? undefined,
    });
  }
  return out;
}

// QualityRecord と QualityMeasurementItem を結合し、QualityMeasurement 形式へ。
export function deriveQualityMeasurements(
  db: Pick<ExtendedDB, "qualityRecords" | "qualityMeasurementItems">,
): DerivedQualityMeasurement[] {
  const itemById = new Map<number, ExtendedDB["qualityMeasurementItems"][number]>();
  for (const qmi of db.qualityMeasurementItems) {
    itemById.set(qmi.id, qmi);
  }
  const out: DerivedQualityMeasurement[] = [];
  for (const qr of db.qualityRecords) {
    if (qr.deleted_at != null) {
      continue;
    }
    const item = itemById.get(qr.quality_measurement_item_id);
    const kind = inferMeasurementKind(item?.name);
    const measurement: DerivedQualityMeasurement = {
      id: qr.id,
      work_id: "", // QualityRecord に work_id は無い。帳票側で construction_id から解決する。
      construction_id: qr.construction_id,
      construction_plan_area_id: qr.construction_plan_area_id,
      measured_at: epochToIsoOrEmpty(qr.measured_at),
      measurement_kind: kind,
      material_id: item?.material_id != null ? String(item.material_id) : undefined,
      judgment: normalizeJudgment(qr.judgment),
      remarks: undefined,
      timing_category: item?.timing_category ?? undefined,
    };
    // value を kind に応じてそれぞれのフィールドへ振り分ける。
    const v = qr.value ?? undefined;
    if (kind === "carry_in_temp_humid" || kind === "construction_temp_humid") {
      // 温湿度の項目名で温度/湿度どちらかを判別。
      if (item?.name?.includes("湿")) {
        measurement.humidity = v;
      } else {
        measurement.temperature = v;
      }
    } else if (kind === "moisture") {
      measurement.moisture_pct = v;
    } else if (kind === "thickness") {
      measurement.thickness_um = v;
    } else if (kind === "mix_actual") {
      // QualityRecord.value は単一値のため主材料側にのみ格納。
      // 副材料側 (mix_actual_sub) は復元不能なため undefined のまま。
      measurement.mix_actual_main = v;
      measurement.mix_actual_sub = undefined;
    } else if (kind === "pot_life") {
      measurement.pot_life_minutes = v;
    }
    out.push(measurement);
  }
  return out;
}
